// This utility provides functions for streaming file uploads with progress tracking

/**
 * Enhance the existing FormData with streaming file upload capability
 * @param {FormData} formData - The existing form data with all metadata and files
 * @param {String} token - Authentication token
 * @param {String} url - API endpoint
 * @param {Object} options - Additional options
 * @param {Function} options.onProgress - Optional callback for progress updates
 * @param {Function} options.onError - Optional callback for error handling
 * @param {Function} options.onAbort - Optional callback for upload abort
 * @returns {Promise} - Resolves with server response
 */
export const uploadFormDataWithProgress = (
  formData,
  token,
  url,
  options = {}
) => {
  const { onProgress, onError, onAbort } = options;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Setup progress tracking
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        const percentComplete = (event.loaded / event.total) * 100;
        if (onProgress) onProgress(percentComplete);
      }
    });

    // Setup completion handler
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch (error) {
          reject(new Error("Invalid response format"));
        }
      } else {
        try {
          const errorData = JSON.parse(xhr.responseText);
          const error = new Error(
            errorData.detail || errorData.status || `HTTP error ${xhr.status}`
          );
          error.statusCode = xhr.status;
          error.response = errorData;
          if (onError) onError(error);
          reject(error);
        } catch (e) {
          const error = new Error(`HTTP error ${xhr.status}`);
          error.statusCode = xhr.status;
          if (onError) onError(error);
          reject(error);
        }
      }
    });

    // Setup error handler
    xhr.addEventListener("error", () => {
      const error = new Error("Network error occurred");
      if (onError) onError(error);
      reject(error);
    });

    // Setup abort handler
    xhr.addEventListener("abort", () => {
      const error = new Error("Upload aborted");
      if (onAbort) onAbort();
      reject(error);
    });

    // Open connection and set headers
    xhr.open("POST", url);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    // Extract file metadata for headers
    // Add filename headers for all files to enable streaming backend support
    const fileHeaders = {};

    // First check for multiple files in formData
    let multipleFiles = false;
    let fileCount = 0;

    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        if (key.startsWith("file") && key !== "file") {
          multipleFiles = true;
          const index = key.replace("file", "");
          fileHeaders[`filename${index}`] = encodeURIComponent(value.name);
          fileCount++;
        } else if (key === "file") {
          fileHeaders["filename"] = encodeURIComponent(value.name);
        }
      }
    }

    // Set filename headers
    for (const [key, value] of Object.entries(fileHeaders)) {
      xhr.setRequestHeader(key, value);
    }

    // Store XHR for potential abort
    if (options.setXhrRef) {
      options.setXhrRef(xhr);
    }

    // Send the complete form data
    xhr.send(formData);
  });
};

/**
 * Helper function to prepare FormData optimized for streaming upload
 * @param {Object} data - Object containing file and metadata
 * @returns {FormData} - Properly structured FormData for streaming upload
 */
export const prepareFormDataForStreaming = (data) => {
  const formData = new FormData();

  // Handle datasets/files specially for streaming
  if (data.datasets && Array.isArray(data.datasets)) {
    // Multiple files case
    data.datasets.forEach((dataset, index) => {
      if (dataset instanceof File || (dataset.filedata && dataset.fileName)) {
        // If it's a File object, append directly
        if (dataset instanceof File) {
          formData.append(`file${index}`, dataset);
        }
        // If it has filedata as base64, convert to File and append
        else if (dataset.filedata && typeof dataset.filedata === "string") {
          try {
            // Convert base64 to File if needed
            const fileContent = extractBase64Content(dataset.filedata);
            const file = base64ToFile(fileContent, dataset.fileName);
            formData.append(`file${index}`, file);
          } catch (error) {
            console.error("Error converting base64 to file:", error);
          }
        }
      }
    });
  } else if (data.datasets && typeof data.datasets === "object") {
    // Single file case
    const dataset = data.datasets;
    if (dataset.filedata && typeof dataset.filedata === "string") {
      try {
        const fileContent = extractBase64Content(dataset.filedata);
        const file = base64ToFile(fileContent, dataset.fileName);
        formData.append("file", file);
      } catch (error) {
        console.error("Error converting base64 to file:", error);
      }
    }
  }

  // Handle other properties
  for (const [key, value] of Object.entries(data)) {
    // Skip datasets as we've handled them specially
    if (key === "datasets") continue;

    if (value === null || value === undefined) continue;

    // Handle arrays and objects
    if (typeof value === "object" && !(value instanceof File)) {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value);
    }
  }

  return formData;
};

/**
 * Extract base64 content from a data URL or encoded string
 * @param {String} base64String - The base64 string to process
 * @returns {String} - Clean base64 content
 */
const extractBase64Content = (base64String) => {
  if (base64String.includes("base64,")) {
    return base64String.split("base64,")[1];
  }
  return base64String;
};

/**
 * Convert base64 string to a File object
 * @param {String} base64 - The base64 content
 * @param {String} filename - The name for the file
 * @returns {File} - A File object
 */
const base64ToFile = (base64, filename) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const blob = new Blob([bytes.buffer]);
  return new File([blob], filename, { type: getMimeType(filename) });
};

/**
 * Guess MIME type from filename
 * @param {String} filename - The filename
 * @returns {String} - The MIME type
 */
const getMimeType = (filename) => {
  const ext = filename.split(".").pop().toLowerCase();
  const mimeTypes = {
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    txt: "text/plain",
    csv: "text/csv",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    svg: "image/svg+xml",
    mp3: "audio/mpeg",
    mp4: "video/mp4",
    json: "application/json",
    xml: "application/xml",
    zip: "application/zip",
  };
  return mimeTypes[ext] || "application/octet-stream";
};
